import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Button, PageHeader, Textfield, PasswordConditions, PasswordValidation, validatePassword, isPasswordValid, doPasswordsMatch } from '@flexo/atoms';
import styles from './forgot-password-mobile.module.scss';
import { useNavigate } from 'react-router-dom';
import { FirebaseService, httpService, IconWrapper } from '@flexo/general';
import { useTranslation } from 'react-i18next';

import { Player } from '@lottiefiles/react-lottie-player';
import { useDispatch } from 'react-redux';
import { ALERT } from '@flexo/reducers';
import { Keyboard } from '@capacitor/keyboard';
import { KeyboardAwareLayout } from '@flexo/organisms';

export enum ForgotPasswordStage {
  EnterEmail = 1,
  EnterRecoveryCode = 2,
  ResetPassword = 3,
}

export interface InputErrors {
  email: boolean;
  code: boolean;
  newPassword: boolean;
  confirmPassword: boolean;
}

export function ForgotPassword({ containedLabelTextfield }: any) {
  const ENV = (import.meta as any).env;

  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const browserLocale =
    navigator.language?.split('-')[0] || ENV.VITE_APP_LANGUAGE || 'en';

  const [email, setEmail] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [currentStage, setCurrentStage] = useState<ForgotPasswordStage>(
    ForgotPasswordStage.EnterEmail
  );
  const [hasResent, setHasResent] = useState(false);

  const loader = '/loaderLottie.json';
  const [loading, setLoading] = useState(false);

  const [inputType, setInputType] = useState({
    newPassword: 'password',
    confirmPassword: 'password',
  });
  const [icon, setIcon] = useState({
    newPassword: 'visibility',
    confirmPassword: 'visibility',
  });

  const [passwordValidation, setPasswordValidation] =
    useState<PasswordValidation>({
      lengthValid: false,
      hasNumber: false,
      hasSpecialChar: false,
      hasUpperCase: false,
      hasLowerCase: false,
      passwordsMatch: false,
    });

  const [errors, setErrors] = useState<InputErrors>({
    email: false,
    code: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [passwordBlur, setPasswordBlur] = useState(false);
  const [blur, setBlur] = useState({
    email: false,
    code: false,
    confirmPassword: false,
    newPassword: false,
  });

  // Create refs for all input fields
  const emailRef = useRef<HTMLInputElement>(null);
  const recoveryCodeRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);

  // Clean up keyboard on unmount and reset resent state
  useEffect(() => {
    return () => {
      Keyboard.hide();
      setHasResent(false); // Reset resent state on unmount
    };
  }, []);

  // Reset resent state when stage changes away from recovery code
  useEffect(() => {
    if (currentStage !== ForgotPasswordStage.EnterRecoveryCode) {
      setHasResent(false);
    }
  }, [currentStage]);

  // Focus appropriate input field when stage changes
  useEffect(() => {
    // Using a timeout to ensure DOM is ready and layout is stable
    const focusTimer = setTimeout(() => {
      let targetRef: React.RefObject<HTMLInputElement> | null = null;

      if (currentStage === ForgotPasswordStage.EnterEmail) {
        targetRef = emailRef;
      } else if (currentStage === ForgotPasswordStage.EnterRecoveryCode) {
        targetRef = recoveryCodeRef;
      } else if (currentStage === ForgotPasswordStage.ResetPassword) {
        targetRef = passwordRef;
      }

      if (targetRef?.current) {
        targetRef.current.focus();
      }
    }, 250);

    return () => clearTimeout(focusTimer);
  }, [currentStage]);

  const isEmail = useCallback((email: string) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    return emailRegex.test(email);
  }, []);

  // Pure validation functions for internal use (no side effects)
  const isValidEmail = useCallback(
    (email: string) => {
      return isEmail(email);
    },
    [isEmail]
  );

  const isValidRecoveryCode = useCallback((code: string) => {
    return code.length >= 4;
  }, []);

  const handlePasswordValidation = useCallback(
    (password: string) => {
      const validation = validatePassword(password, confirmPassword);
      setPasswordValidation(validation);
    },
    [confirmPassword]
  );

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      switch (name) {
        case 'email':
          setEmail(value);
          break;
        case 'recoveryCode':
          setRecoveryCode(value);
          break;
        case 'newPassword':
          setNewPassword(value);
          handlePasswordValidation(value);
          break;
        case 'confirmPassword':
          setConfirmPassword(value);
          setPasswordValidation((prev) => ({
            ...prev,
            passwordsMatch: newPassword === value,
          }));
          break;
      }
    },
    [newPassword, confirmPassword, handlePasswordValidation, recoveryCode]
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      if (name === 'newPassword') setPasswordBlur(true);

      if (name === 'confirmPassword') {
        setErrors({
          ...errors,
          confirmPassword: newPassword !== value,
        });
        setBlur({
          ...blur,
          confirmPassword: true,
        });
      }

      if (name !== 'email' || !value.length) return;
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: !isEmail(value),
      }));
    },
    [newPassword, confirmPassword, errors, blur, isEmail]
  );
  const handleFocus = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      const { name } = event.target;

      if (name === 'email') {
        setErrors((prevErrors) => ({
          ...prevErrors,
          email: false,
        }));
      }

      if (name === 'newPassword') {
        setPasswordBlur(false);
      }

      if (name === 'confirmPassword') {
        setErrors((prevErrors) => ({
          ...prevErrors,
          confirmPassword: false,
        }));
      }
    },
    [blur]
  );

  const requestRecoveryCode = useCallback(async () => {
    if (!isEmail(email)) return;
    const firebaseService = new FirebaseService();
    const idToken = await firebaseService.getIdToken();
    setLoading(true);
    try {
      const response = await httpService.post({
        url: `${ENV.VITE_APP_BASE_URL}/auth/v1_1/password-forgot`,
        data: { email: email, locale: browserLocale },
        idToken: idToken,
      });
      if (response.status === 200)
        setCurrentStage(ForgotPasswordStage.EnterRecoveryCode);
    } catch (error) {
      dispatch(
        ALERT.SetAlert({ value: { type: 'BANNER_UNEXPECTED_ERROR' } as any })
      );
    } finally {
      setLoading(false);
    }
  }, [email, ENV.VITE_APP_BASE_URL, isEmail, dispatch, browserLocale]);

  const verifyRecoveryCode = useCallback(async () => {
    setLoading(true); // Start loading
    const firebaseService = new FirebaseService();

    const idToken = await firebaseService.getIdToken();
    try {
      const response = await httpService.post({
        url: `${ENV.VITE_APP_BASE_URL}/auth/v1_1/validate-reset-code`,
        data: {
          email: email,
          reset_code: recoveryCode,
        },
      });

      if (response.data.valid)
        setCurrentStage(ForgotPasswordStage.ResetPassword);
      else
        dispatch(
          ALERT.SetAlert({
            payload: {
              value: {
                type: 'BANNER_INVALID_CREDENTIALS',
              } as any,
            },
          } as any)
        );

      return response;
    } catch (error) {
      // console.warn(error);

      dispatch(
        ALERT.SetAlert({
          payload: {
            value: {
              type: 'BANNER_UNEXPECTED_ERROR',
            } as any,
          },
        } as any)
      );
    } finally {
      setLoading(false); // Stop loading after action completes
    }
  }, [email, recoveryCode, ENV.VITE_APP_BASE_URL]);

  const resetPassword = useCallback(async () => {
    const firebaseService = new FirebaseService();
    const idToken = await firebaseService.getIdToken();
    if (newPassword !== confirmPassword) return;
    setLoading(true);

    try {
      const response = await httpService.post({
        url: `${ENV.VITE_APP_BASE_URL}/auth/v1_1/password-change-confirm`,
        data: {
          email: email,
          reset_code: recoveryCode,
          new_password: newPassword,
          tenant_id: 'internal-foj18',
        },
        idToken: idToken,
      });
      if (response.status === 200)
        localStorage.setItem('passwordUpdated', 'true');
      sessionStorage.setItem('passwordUpdated', 'true');
      navigate('/login');


    } catch (error: any) {
      console.error('Password reset failed:', error);

      // Check if error is related to invalid recovery code or email combination
      if (
        error?.response?.status === 400 ||
        error?.response?.data?.message?.includes('reset_code') ||
        error?.response?.data?.message?.includes('Invalid')
      ) {
        // Invalid recovery code/email combination - go back to recovery code stage with error
        setCurrentStage(ForgotPasswordStage.EnterRecoveryCode);
        setErrors((prevErrors) => ({ ...prevErrors, code: true }));
        dispatch(
          ALERT.SetAlert({ value: { type: 'BANNER_UNEXPECTED_ERROR' } as any })
        );
      } else {
        // Other errors - show generic banner
        dispatch(
          ALERT.SetAlert({ value: { type: 'BANNER_UNEXPECTED_ERROR' } as any })
        );
      }
    } finally {
      setLoading(false);
    }


  }, [
    email,
    recoveryCode,
    newPassword,
    confirmPassword,
    ENV.VITE_APP_BASE_URL,
    navigate,
    dispatch,
  ]);

  const togglePasswordVisibility = useCallback((name: string) => {
    setInputType((p) => ({
      ...p,
      [name]: p[name] === 'password' ? 'text' : 'password',
    }));
    setIcon((p) => ({
      ...p,
      [name]: p[name] === 'visibility' ? 'hide-visibility' : 'visibility',
    }));
  }, []);

  const handleResendCode = useCallback(async () => {
    await requestRecoveryCode();
    setHasResent(true);
  }, [requestRecoveryCode]);

  const getSubtitleText = useCallback(() => {
    if (currentStage === ForgotPasswordStage.EnterEmail)
      return t('pages.forgotten_password.subtitle');
    if (currentStage === ForgotPasswordStage.EnterRecoveryCode) {
      // For recovery code stage, we'll handle the subtitle in a custom component
      return '';
    }
    if (currentStage === ForgotPasswordStage.ResetPassword)
      return t('pages.forgotten_password.subtitle_change', { email });
    return '';
  }, [currentStage, t, email]);

  const renderCustomSubtitle = useCallback(() => {
    if (currentStage !== ForgotPasswordStage.EnterRecoveryCode) return null;

    const line1 = t('pages.forgotten_password.recoveryCode_subtitle.line_1');
    const line2 = t('pages.forgotten_password.recoveryCode_subtitle.line_2');
    const line3 = t('pages.forgotten_password.recoveryCode_subtitle.line_3');

    return (
      <div
        className="paragraph"
        style={{ marginTop: '8px', color: 'var(--ion-color-medium)' }}
      >
        {line1} <strong style={{ fontFamily: 'Flexo medium' }}>{email}</strong>
        {line2} {line3} 
        {`${line2} ${line3} `}
        {hasResent ? (
          <span
            style={{
              fontFamily: 'Flexo medium',
              color: 'var(--hive-greyscale-700)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            {t('pages.forgotten_password.recoveryCode_subtitle.resentLabel')}
            <IconWrapper
              iconName="check-encircled_outline"
              style={{
                color: '#009E62',
                width: '16px',
                height: '16px',
                fontSize: '16px',
                lineHeight: '1',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            />
          </span>
        ) : (
          <button
            onClick={handleResendCode}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              color: '#009E62',
              fontFamily: 'Flexo medium',
              cursor: 'pointer',
              fontSize: 'inherit',
            }}
          >
            {t('pages.forgotten_password.recoveryCode_subtitle.buttonLabel')}
          </button>
        )}
      </div>
    );
  }, [currentStage, t, email, hasResent, handleResendCode]);
  const stageConfig = useMemo(
    () => ({
      [ForgotPasswordStage.EnterEmail]: {
        buttonLabel: t('pages.forgotten_password.sendButton'),
        action: requestRecoveryCode,
        isDisabled: () => !email || !isValidEmail(email) || loading,
        inputComponent: (
          <Textfield
            name="email"
            ref={emailRef}
            value={email}
            placeholder={
              t('pages.forgotten_password.email_placeholder') as string
            }
            label={t('pages.forgotten_password.email') as string}
            onChange={handleInputChange}
            type="email"
              variant="containedLabel"
            autoComplete="email"
            error={errors.email}
            errorDescription={
              errors.email
                ? (t('pages.forgotten_password.email_error') as string)
                : ''
            }
            onBlur={handleBlur}
            onFocus={handleFocus}
            key="email-input"
          />
        ),
      },
      [ForgotPasswordStage.EnterRecoveryCode]: {
        buttonLabel: t('pages.forgotten_password.verifyButton'),
        action: verifyRecoveryCode,
        isDisabled: () =>
          !recoveryCode || !isValidRecoveryCode(recoveryCode) || loading,
        inputComponent: (
          <Textfield
            name="recoveryCode"
            ref={recoveryCodeRef}
            value={recoveryCode}
            placeholder={
              t('pages.forgotten_password.recoveryCode_placeholder') as string
            }
            label={t('pages.forgotten_password.recoveryCode') as string}
            onChange={handleInputChange}
            type="text"
              variant="containedLabel"
            error={errors.code && blur.code}
            errorDescription={
              errors.code && blur.code
                ? (t('pages.forgotten_password.code_error') as string)
                : ''
            }
            onBlur={handleBlur}
            onFocus={handleFocus}
            key="code-input"
          />
        ),
      },
      [ForgotPasswordStage.ResetPassword]: {
        buttonLabel: t('pages.forgotten_password.resetButton'),
        action: resetPassword,
        isDisabled: () => {
          if (!newPassword || !confirmPassword) return true;
          return (
            !isPasswordValid(newPassword) ||
            !doPasswordsMatch(newPassword, confirmPassword)
          );
        },
        inputComponent: (
          <>
            <Textfield
              name="newPassword"
              ref={passwordRef}
              value={newPassword}
              placeholder={
                t('pages.forgotten_password.newPassword_placeholder') as string
              }
              label={t('pages.forgotten_password.newPassword') as string}
              onChange={handleInputChange}
              type={inputType.newPassword}
                variant="containedLabel"
              icon={icon.newPassword}
              onIconClick={() => togglePasswordVisibility('newPassword')}
              onBlur={handleBlur}
              onFocus={handleFocus}
              error={errors.newPassword && blur.newPassword}
              errorDescription={
                errors.newPassword && blur.newPassword
                  ? 'Please review the password criteria'
                  : ''
              }
              key="password-input"
            />
            <PasswordConditions
              validation={passwordValidation}
              isFocused={!passwordBlur}
              conditionClassName={styles.ForgotPassword__Conditions}
            />
            <Textfield
              name="confirmPassword"
              ref={confirmPasswordRef}
              value={confirmPassword}
              placeholder={
                t(
                  'pages.forgotten_password.confirmPassword_placeholder'
                ) as string
              }
              label={t('pages.forgotten_password.confirmPassword') as string}
              onChange={handleInputChange}
              type={inputType.confirmPassword}
                variant="containedLabel"
              icon={icon.confirmPassword}
              onIconClick={() => togglePasswordVisibility('confirmPassword')}
              onBlur={handleBlur}
              error={errors.confirmPassword}
              errorDescription={
                errors.confirmPassword
                  ? 'Please review the password criteria'
                  : ''
              }
              onFocus={handleFocus}
              key="confirm-password-input"
            />
          </>
        ),
      },
    }),
    [
      t,
      email,
      isValidEmail,
      isValidRecoveryCode,
      isPasswordValid,
      doPasswordsMatch,
      handleInputChange,
      handleBlur,
      errors,
      recoveryCode,
      newPassword,
      confirmPassword,
      passwordValidation,
      inputType,
      icon,
      togglePasswordVisibility,
      blur,
      handleFocus,
      passwordBlur,
      requestRecoveryCode,
      verifyRecoveryCode,
      resetPassword,
      loading,
    ]
  );

  const currentStageConfig = useMemo(
    () => stageConfig[currentStage],
    [stageConfig, currentStage]
  );

  const handleKeyPress = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Enter' && !currentStageConfig.isDisabled() && !loading)
        currentStageConfig.action();
    },
    [currentStageConfig, loading]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleKeyPress]);

  // Header component for the keyboard-aware layout
  const headerComponent = (
    <div className={styles.ForgotPassword__Header}>
      <PageHeader
        title={t('pages.forgotten_password.title')}
        subtitle={getSubtitleText()}
        back={t('pages.forgotten_password.backButton') || ''}
        backAction={() => {
          if (currentStage === ForgotPasswordStage.EnterEmail) navigate(-1);
          else setCurrentStage((prevStage) => prevStage - 1);
        }}
      />
      {renderCustomSubtitle()}
    </div>
  );

  // Footer component with the action button
  const footerComponent = (
    <div className={styles.ForgotPassword__Footer}>
      <Button
        onClick={currentStageConfig.action}
        variant="primary"
        color="secondary"
        className={styles.ForgotPassword__Button}
        label={
          loading ? (
            <Player autoplay loop src={loader} style={{ height: 20 }} />
          ) : (
            currentStageConfig.buttonLabel
          )
        }
        disabled={currentStageConfig.isDisabled() || loading}
      />
    </div>
  );

  // Main content with input fields
  const mainContent = (
    <div className={styles.ForgotPassword__Inputs}>
      <div key={`stage-${currentStage}`}>
        {currentStageConfig.inputComponent}
      </div>
    </div>
  );

  return (
    <div className={styles.ForgotPassword}>
      <KeyboardAwareLayout
        header={headerComponent}
        footer={footerComponent}
        extraPadding={24}
        contentClassName={styles.ForgotPassword__Content}
      >
        {mainContent}
      </KeyboardAwareLayout>
    </div>
  );
}

export default ForgotPassword;
